import { PassioRequestHeaders, type PassioRequestHeadersInit } from "@passio/api-client";
import { createStore, useStore } from "./store";

const KEY = "passio-demo.client-settings.v1";

let defaults: PassioRequestHeadersInit = { localizationIso: "en", productLocalization: "en-US", productImages: true };

function load(): PassioRequestHeaders | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? PassioRequestHeaders.fromJSON(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

/** Global client settings: the optional input headers sent with every Passio call. */
export const clientSettings = createStore<PassioRequestHeaders>(load() ?? new PassioRequestHeaders(defaults));

export function getClientHeaders(): PassioRequestHeaders {
  return clientSettings.get();
}

export function setClientHeaders(next: PassioRequestHeaders) {
  clientSettings.set(next);
  localStorage.setItem(KEY, JSON.stringify(next.toJSON()));
}

/** Admin-configured defaults; applied unless the user already saved their own. */
export function applyServerDefaults(init: PassioRequestHeadersInit) {
  defaults = init;
  if (!localStorage.getItem(KEY)) clientSettings.set(new PassioRequestHeaders(init));
}

export function resetClientHeaders() {
  localStorage.removeItem(KEY);
  clientSettings.set(new PassioRequestHeaders(defaults));
}

export function useClientHeaders(): PassioRequestHeaders {
  return useStore(clientSettings);
}

const INSTRUCTIONS_KEY = "passio-demo.app-instructions.v1";

function loadInstructions(): string {
  try {
    return localStorage.getItem(INSTRUCTIONS_KEY) ?? "";
  } catch {
    return "";
  }
}

/** Standing instructions a B2B app adds to every AI call's `message` (e.g. diet program rules). */
export const appInstructions = createStore<string>(loadInstructions());

export function setAppInstructions(text: string) {
  appInstructions.set(text);
  if (text.trim()) localStorage.setItem(INSTRUCTIONS_KEY, text);
  else localStorage.removeItem(INSTRUCTIONS_KEY);
}

export const useAppInstructions = () => useStore(appInstructions);

/** The `message` field for an AI route: the app's standing instructions, then the user's note. */
export function composeMessage(note: string, instructions = appInstructions.get()): string | undefined {
  const app = instructions.trim();
  const user = note.trim();
  if (app && user) return `${app}\n\nUser note: ${user}`;
  return app || user || undefined;
}

/** Client settings drawer, so any page can open it. */
export const settingsDrawer = createStore(false);
export const openClientSettings = () => settingsDrawer.set(true);
export const closeClientSettings = () => settingsDrawer.set(false);
export const useSettingsDrawer = () => useStore(settingsDrawer);
